import React, { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { PaytrieAPI } from '@paytrie/client'

import { ErrorBlockText, RowAlign } from '@/components/widget-components'
import { Button } from '@/components/Button/Button'
import { BirthdayInput } from '@/components/Select/BirthdayInput'
import { LoadingIcon } from '@/components/LoadingIcon'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { usePageStateStore, useSignupStore } from '@/reducers'
import { normalizeDob } from '@/utils/dob'
import {
  trackOnboardingStepCompleted,
  trackOnboardingStepFailed,
  trackOnboardingStepSubmitted,
  trackOnboardingStepViewed,
} from '@/utils/analytics'
import { formatPostalCode } from './address'
import { AddressFields } from './AddressFields'
import { TextField } from '@/components/FormField'
import {
  buildPersonalInfoSchema,
  PERSONAL_INFO_DEFAULTS,
  type PersonalInfoValues,
} from './SignupPersonalInformation.logic'

const toDefaultValues = (user?: PaytrieAPI.CurrentUser): PersonalInfoValues => {
  if (!user) return PERSONAL_INFO_DEFAULTS
  return {
    firstName: user.firstName ?? '',
    lastName: user.lastName ?? '',
    dob: user.dob ?? '',
    address1: user.addressLine1 ?? '',
    address2: user.addressLine2 ?? '',
    city: user.city ?? '',
    province: (user.province ?? '').toUpperCase(),
    postal: formatPostalCode(user.postalCode ?? ''),
  }
}

const SignupPersonalInformationInner = React.memo(
  function SignupPersonalInformationInner({
    user,
  }: {
    user?: PaytrieAPI.CurrentUser
  }) {
    const { t } = useTranslation()
    const navigate = useNavigate()

    const { nextState } = useSignupStore()
    const queryClient = useQueryClient()

    const schema = useMemo(() => buildPersonalInfoSchema(t), [t])
    const methods = useForm<PersonalInfoValues>({
      resolver: zodResolver(schema),
      defaultValues: toDefaultValues(user),
      mode: 'onTouched',
    })
    const {
      control,
      handleSubmit,
      setError,
      formState: { errors, isValid, isSubmitting },
    } = methods

    const onValid = async (values: PersonalInfoValues) => {
      trackOnboardingStepSubmitted('personal_information')
      try {
        const res = await PaytrieAPI.updateCurrentUser({
          firstName: values.firstName,
          lastName: values.lastName,
          dob: normalizeDob(values.dob),
          addressLine1: values.address1,
          addressLine2: values.address2 || null,
          city: values.city,
          province:
            values.province.toLowerCase() as PaytrieAPI.UpdateCurrentUserBodyProvince,
          postalCode: values.postal.replace(/\s/g, '').toLowerCase(),
          currentFlow:
            PaytrieAPI.UpdateCurrentUserBodyCurrentFlow.PERSONAL_INFO,
        })

        if (res.status !== 200) {
          const message =
            'errors' in res.data && res.data.errors?.[0]?.message
              ? res.data.errors[0].message
              : t('personalInfo.error.unexpected')
          setError('root', { message })
          trackOnboardingStepFailed('personal_information', 'unable_to_process')
          return
        }
      } catch {
        setError('root', { message: t('personalInfo.error.unexpected') })
        trackOnboardingStepFailed('personal_information', 'unable_to_process')
        return
      }

      await queryClient.invalidateQueries({
        queryKey: ['v2', 'users', 'me'],
      })

      nextState()
      trackOnboardingStepCompleted('personal_information')
      navigate('/SignupOccupation')
    }

    const onInvalid = () =>
      trackOnboardingStepFailed('personal_information', 'validation_failed')

    return (
      <FormProvider {...methods}>
        <form
          className="flex flex-col gap-5"
          onSubmit={handleSubmit(onValid, onInvalid)}
          data-testid="container-personal-info-form"
        >
          <div className="text-lg font-normal uppercase">
            {t('personalInfo.title')}
          </div>

          <div className="grid grid-cols-2 gap-2 mb-4">
            <TextField<PersonalInfoValues>
              name="firstName"
              placeholder={t('personalInfo.firstName.placeholder')}
              testId="input-first-name"
              autoFocus
            />
            <TextField<PersonalInfoValues>
              name="lastName"
              placeholder={t('personalInfo.lastName.placeholder')}
              testId="input-last-name"
            />
            <div className="col-span-2">
              <RowAlign>
                <Controller
                  control={control}
                  name="dob"
                  render={({ field }) => (
                    <BirthdayInput
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      showFieldErrors
                    />
                  )}
                />
              </RowAlign>
            </div>

            <div className="col-span-2">
              <AddressFields secondaryColumns={2} />
            </div>
          </div>

          <ErrorBlockText data-testid="error-form-message">
            {errors.root?.message ?? ''}
          </ErrorBlockText>
          <Button
            className="inline-flex items-center py-8 px-4 w-full justify-center lg:gap-2"
            type="submit"
            disabled={!isValid || isSubmitting}
            data-testid="btn-continue"
          >
            {t('personalInfo.submit')}
          </Button>
        </form>
      </FormProvider>
    )
  }
)

export default function SignupPersonalInformation() {
  const authToken = usePageStateStore.use.authToken() || ''

  const { data: loggedInUser, isLoading } = useCurrentUser(!!authToken)

  useEffect(() => {
    trackOnboardingStepViewed('personal_information')
  }, [])

  if (isLoading) {
    return (
      <div
        className="my-auto flex justify-center"
        data-testid="container-spinner"
      >
        <LoadingIcon />
      </div>
    )
  }

  return <SignupPersonalInformationInner user={loggedInUser} />
}
